/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import { apiMessageOrGeneric } from "../../utils/genericErrorMessages.js";
/**
 * @fileoverview Buyer Company logic module.
 * Handles buyer company CRUD operations, filtering, sorting, pagination,
 * and Excel file processing for bulk company imports.
 * @module logic/buyerCompany
 */

import { refreshToken, signOut } from "../../utils/common.js";
import { validateBuyerCompaniesExcelData } from "../../utils/excelValidation.js";
import { getCustomerCompanyUsersByCompanyId } from "../customerCompanyUser/index.js";

/**
 * Fetches all buyer companies from the API.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of buyer company objects
 */
export const getBuyerCompanies = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}buyerCompany`,
      options,
    );
    const result = await response.json();

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getBuyerCompanies(navigate);
    }
    if (!response.ok || !Array.isArray(result.data)) {
      return [];
    }
    return result.data;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Fetches all buyer companies basic info  from the API.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of buyer company objects
 */
export const getBuyerCompaniesBasicInfo = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}buyerCompany/all`,
      options,
    );
    const result = await response.json();

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getBuyerCompaniesBasicInfo(navigate);
    }
    if (!response.ok || !Array.isArray(result.data)) {
      return [];
    }
    return result.data;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Sorts buyer companies alphabetically by name.
 * @param {Array} buyerCompanies - Array of buyer company objects
 * @param {Object} sortConfig - Sort configuration
 * @param {string} sortConfig.direction - Sort direction ('ascending' or 'descending')
 * @returns {Array} Sorted array of buyer companies
 */
export const sortBuyerCompanies = (buyerCompanies, sortConfig) => {
  if (!Array.isArray(buyerCompanies) || buyerCompanies.length === 0) {
    return [];
  }

  return [...buyerCompanies].sort((a, b) => {
    const nameA = a.nombre ? a.nombre.toLowerCase() : "";
    const nameB = b.nombre ? b.nombre.toLowerCase() : "";
    if (nameA < nameB) {
      return sortConfig.direction === "ascending" ? -1 : 1;
    }
    if (nameA > nameB) {
      return sortConfig.direction === "ascending" ? 1 : -1;
    }
    return 0;
  });
};

/**
 * Filters buyer companies by name, empresa, or RFC.
 * @param {Array} buyerCompanies - Array of buyer company objects
 * @param {string} searchQuery - Search string to filter by
 * @returns {Array} Filtered array of buyer companies
 */
export const filterBuyerCompanies = (buyerCompanies, searchQuery) => {
  if (!searchQuery) {
    return buyerCompanies;
  }
  return buyerCompanies.filter(
    (buyerCompany) =>
      buyerCompany.nombre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      buyerCompany.empresa?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      buyerCompany.RFC?.toLowerCase().includes(searchQuery.toLowerCase()),
  );
};

/**
 * Paginates buyer companies for display.
 * @param {Array} buyerCompanies - Array of buyer company objects
 * @param {number} currentPage - Current page number (1-indexed)
 * @param {number} resultsLimit - Number of results per page
 * @returns {Array} Paginated slice of buyer companies
 */
export const paginateBuyerCompanies = (
  buyerCompanies,
  currentPage,
  resultsLimit,
) => {
  return buyerCompanies.slice(
    (currentPage - 1) * resultsLimit,
    currentPage * resultsLimit,
  );
};

/**
 * Fetches a single buyer company by ID.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @param {string} id - Buyer company identifier
 * @returns {Promise<Object|null>} Buyer company data or null on error
 */
export const getBuyerCompany = async (navigate, id) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}buyerCompany/${id}`,
      options,
    );
    const result = await response.json();

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getBuyerCompany(navigate, id);
    }
    return result.data;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return null;
  }
};

/**
 * Creates a new buyer company.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @param {Object} companyData - Company data to create
 * @param {string} companyData.razonSocial - Company legal name
 * @param {string} companyData.RFC - Company RFC
 * @returns {Promise<Object|null>} Created company data or null on error
 * @throws {Error} If creation fails
 */
export const createBuyerCompany = async (navigate, companyData) => {
  const buyerCompanyData = {
    ...companyData,
    idUsuario: localStorage.getItem("userId"),
  };

  const response = await fetch(
    `${process.env.REACT_APP_ROOT_API}buyerCompany`,
    {
      method: "POST",
      headers: {
        "Content-Type": CONTENT_TYPE_JSON,
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(buyerCompanyData),
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    if (errorData?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await createBuyerCompany(navigate, companyData);
    }
    throw new Error(
      apiMessageOrGeneric(errorData.message, "FAILED_TO_CREATE_BUYER_COMPANY"),
    );
  }

  return await response.json();
};

/**
 * Updates an existing buyer company.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @param {string} companyId - Buyer company identifier
 * @param {Object} companyData - Updated company data
 * @returns {Promise<Object|null>} Updated company data or null on error
 * @throws {Error} If update fails
 */
export const updateBuyerCompany = async (navigate, companyId, companyData) => {
  const buyerCompanyData = {
    ...companyData,
    idUsuario: localStorage.getItem("userId"),
  };

  const response = await fetch(
    `${process.env.REACT_APP_ROOT_API}buyerCompany/${companyId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": CONTENT_TYPE_JSON,
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(buyerCompanyData),
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    if (errorData?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await updateBuyerCompany(navigate, companyId, companyData);
    }
    throw new Error(
      apiMessageOrGeneric(errorData.message, "FAILED_TO_UPDATE_BUYER_COMPANY"),
    );
  }

  return await response.json();
};

/**
 * Creates a user for a buyer company.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @param {string} companyId - Buyer company identifier
 * @param {Object} userData - User data to create
 * @param {string} userData.nombre - User name
 * @param {string} userData.correoElectronico - User email
 * @param {string} userData.cargo - User position/title
 * @param {string} userData.codigoPais - Phone country code
 * @param {string} userData.numeroTelefono - Phone number
 * @returns {Promise<Object|null>} Created user data or null on error
 * @throws {Error} If creation fails
 */
export const createBuyerCompanyUser = async (navigate, companyId, userData) => {
  const userPayload = {
    nombre: userData.nombre,
    correoElectronico: userData.correoElectronico,
    cargo: userData.cargo,
    telefonoCodigoPais: userData.codigoPais,
    telefono: userData.numeroTelefono,
    idEmpresa: companyId,
    activo: false, // Set to false when creating users via Excel
  };

  const response = await fetch(
    `${process.env.REACT_APP_ROOT_API}customerCompanyUser`,
    {
      method: "POST",
      headers: {
        "Content-Type": CONTENT_TYPE_JSON,
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(userPayload),
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    if (errorData?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await createBuyerCompanyUser(navigate, companyId, userData);
    }
    const creationError = new Error(
      apiMessageOrGeneric(
        errorData.message,
        "FAILED_TO_CREATE_BUYER_COMPANY_USER",
      ),
    );
    creationError.existingUser = errorData.existingUser;
    throw creationError;
  }

  return await response.json();
};

const createBuyerCompanyUsers = async (
  navigate,
  companyId,
  users,
  companyName,
  errors,
) => {
  const createdUsers = [];
  for (const userData of users) {
    try {
      const userResult = await createBuyerCompanyUser(
        navigate,
        companyId,
        userData,
      );
      createdUsers.push(userResult);
      logger.info("User created successfully:", { data: userResult });
    } catch (error) {
      errors.push({
        company: companyName,
        user: userData.nombre,
        error: error.message,
        existingUser: error.existingUser,
      });
    }
  }
  return createdUsers;
};

/**
 * Processes an Excel file to import buyer companies and users in bulk.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @param {File} file - Excel file to process
 * @param {Object} validationFunctions - Validation helper functions
 * @param {Function} validationFunctions.parseExcelFile - Excel parsing function
 * @param {Function} t - Translation function
 * @returns {Promise<Object>} Result with success flag, results/errors arrays
 */
export const processExcelFile = async (
  navigate,
  file,
  validationFunctions,
  t,
) => {
  logger.info("processExcelFile", { data: file });

  try {
    const { parseExcelFile } = validationFunctions;
    const excelData = await parseExcelFile(file);
    const validation = await validateBuyerCompaniesExcelData(
      excelData,
      {
        ...validationFunctions,
        getCustomerCompanyUsersByCompanyId,
        navigate,
      },
      t,
    );

    logger.info("validation", { data: validation });
    logger.info("validation.isValid:", { data: validation.isValid });
    logger.info("validation.error:", { data: validation.error });

    if (!validation.isValid) {
      logger.info("Setting excelError:", { data: validation.error });
      logger.info("Type of validation.error:", {
        data: typeof validation.error,
      });
      return {
        success: false,
        error: validation.error,
        invalidRows: validation.invalidRows || [],
      };
    }

    logger.info("validation.invalidRows", { data: validation.invalidRows });

    if (validation.invalidRows && validation.invalidRows.length > 0) {
      logger.info("Setting excelError for invalid rows:", {
        data: validation.error,
      });
      return {
        success: false,
        error: validation.error,
        invalidRows: validation.invalidRows,
      };
    }

    logger.info("validation.validCompanies", {
      data: validation.validCompanies,
    });

    // Create buyer companies with their users
    const results = [];
    const errors = [];

    for (const companyGroup of validation.validCompanies) {
      try {
        let importedCompanyId = companyGroup.existingCompanyId;
        if (!importedCompanyId) {
          const companyResult = await createBuyerCompany(
            navigate,
            companyGroup.companyData,
          );
          importedCompanyId = companyResult?.data?._id;
        }
        if (!importedCompanyId) {
          throw new Error("Failed to create buyer company");
        }
        const createdUsers = await createBuyerCompanyUsers(
          navigate,
          importedCompanyId,
          companyGroup.users,
          companyGroup.companyData.razonSocial,
          errors,
        );
        results.push({
          companyId: importedCompanyId,
          existingCompany: Boolean(companyGroup.existingCompanyId),
          users: createdUsers,
        });
      } catch (error) {
        errors.push({
          company: companyGroup.companyData.razonSocial,
          error: error.message,
        });
      }
    }

    if (errors.length > 0) {
      return {
        success: false,
        error: t("pages.buyerCompanyForm.excelPartialError", {
          count: errors.length,
        }),
        results,
        errors,
      };
    }

    return {
      success: true,
      results,
      errors: [],
    };
  } catch (error) {
    logger.error("Error processing Excel file:", { err: sanitizeError(error) });
    return {
      success: false,
      error: t("pages.buyerCompanyForm.excelProcessingError"),
    };
  }
};
